// TEMPORARY. Delete this module, its test, the README's "Dev gap fill"
// section, the `devGapFill` branch in `select.ts` and the extra alias rule in
// `next.config.mjs` once every SignupApi method is wired to a real backend.
//
// Dev and preview builds can't click through signup while some calls have no
// backend: those reject with SignupNotImplementedError, and the sponsor lookup
// answers not_found for every client because no sponsor config is set there.
// In those builds only, each such call falls back to the local fake, so the
// whole flow can be reviewed. Built calls stay real. Production never applies
// this wrapper and its bundle never contains the fake.

import { isUnknownExpressScriptsCarrier } from '../sponsor-lookup';
import {
  SignupNotImplementedError,
  SignupRequestError,
  type SignupApi,
  type SignupSponsorQuery,
} from './signup-api';

/**
 * The calls with no backend yet. When one is wired, remove it here: the list
 * test fails until the list matches the real API's not-implemented calls.
 */
export const DEV_GAP_FILL_METHODS = [
  'captureConsent',
] as const satisfies readonly (keyof SignupApi)[];

type DevGapFillMethod = (typeof DEV_GAP_FILL_METHODS)[number];

const FILLED_MARKER_KEY = 'galapagos.signup.dev-gap-filled';

// Stands in for the tab marker when session storage is unavailable.
let filledThisPageLoad = false;

function markFilled(): void {
  filledThisPageLoad = true;
  try {
    window.sessionStorage.setItem(FILLED_MARKER_KEY, '1');
  } catch {
    // The in-memory marker still holds for this page load.
  }
}

/**
 * True once a fill-in has answered in this tab session. Conversion events
 * check it, so clicks through fake data never count as real signups.
 */
export function isDevGapFilledSession(): boolean {
  if (filledThisPageLoad) return true;
  try {
    return window.sessionStorage.getItem(FILLED_MARKER_KEY) !== null;
  } catch {
    return false;
  }
}

export function resetDevGapFillForTests(): void {
  filledThisPageLoad = false;
}

function orFake<T>(pending: Promise<T>, fill: () => Promise<T>): Promise<T> {
  return pending.catch((error: unknown) => {
    if (!(error instanceof SignupNotImplementedError)) throw error;
    markFilled();
    return fill();
  });
}

// The API answers the same not_found for an unknown client as for one it has
// no config to look up, so any miss for a named client is treated as the gap.
// A link with no client id is answered in the browser and stays as it is. An
// Express Scripts carrier the portal does not know is left as a miss, so dev
// shows the carrier search the flow falls back to.
function isSponsorGap(query: SignupSponsorQuery, error: unknown): boolean {
  return (
    query.clientId !== undefined &&
    query.clientId !== '' &&
    error instanceof SignupRequestError &&
    error.code === 'not_found' &&
    !isUnknownExpressScriptsCarrier(query, error)
  );
}

/** The real API, with each call that has no backend yet answered by the fake. */
export function withDevGapFill(real: SignupApi, fake: SignupApi): SignupApi {
  const fills: Pick<SignupApi, DevGapFillMethod> = {
    captureConsent: (request) =>
      orFake(real.captureConsent(request), () => fake.captureConsent(request)),
  };
  return {
    ...real,
    ...fills,
    resolveSponsor: (query) =>
      real.resolveSponsor(query).catch((error: unknown) => {
        if (!isSponsorGap(query, error)) throw error;
        markFilled();
        return fake.resolveSponsor(query);
      }),
  };
}
